// src/components/discover/character-card.tsx
"use client";

import type { FC, MouseEvent } from 'react';
import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import type { Character } from '@/lib/types';
import { Card } from '@/components/ui/card';
import { MessageCircle, MoreVertical, Edit3, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useI18n } from '@/context/i18n-context';
import { cn } from '@/lib/utils';
import { memo } from 'react';
import { Skeleton } from "@/components/ui/skeleton";
import { formatLargeNumber } from '@/lib/firebaseUtils';

interface CharacterCardProps {
  character: Character;
  showManagementControls?: boolean;
  onEdit?: (characterId: string) => void;
  onDelete?: (character: Character) => void;
  onToggleVisibility?: (characterId: string) => void;
}

const CharacterCard: FC<CharacterCardProps> = ({
  character,
  showManagementControls = false,
  onEdit,
  onDelete,
  onToggleVisibility
}) => {
  const { t } = useI18n();
  const [isImageLoading, setIsImageLoading] = useState(true);


  const handleDropdownInteraction = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const cardContent = (
    <Card className="bg-[hsl(var(--glass-character-card-bg))] text-[hsl(var(--glass-text))] backdrop-blur-md transition-all duration-300 ease-in-out overflow-hidden h-full flex flex-col group rounded-2xl shadow-lg hover:shadow-primary/30">
      <div className="relative w-full aspect-[2/3] overflow-hidden rounded-2xl">
        {isImageLoading && <Skeleton className="absolute inset-0 h-full w-full rounded-2xl bg-white/5" />}
        <Image
          src={character.imageUrl || `https://placehold.co/300x400.png?text=${character.name?.[0]?.toUpperCase() || 'C'}`}
          alt={character.name}
          fill
          className={cn(
            "object-cover transition-all duration-300 group-hover:scale-105",
            isImageLoading ? 'opacity-0' : 'opacity-100'
          )}
          data-ai-hint={character.imageHint || character.name.toLowerCase()}
          sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, (max-width: 1280px) 20vw, (max-width: 1536px) 14vw, 12.5vw"
          onLoad={() => setIsImageLoading(false)}
          onError={() => setIsImageLoading(false)}
        />

        <div
          className={cn(
            "absolute inset-0 z-10 flex flex-col justify-end p-3 rounded-2xl transition-all duration-300 ease-in-out overflow-hidden",
            "bg-gradient-to-t from-black/80 via-black/40 to-transparent",
          )}
        >
          <h3 className="text-base font-bold text-white drop-shadow-lg" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>{character.name}</h3>
          <div className="overflow-hidden">
             <p className="text-xs text-white/70 line-clamp-3 mt-1 drop-shadow-md" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}>
                {character.description}
              </p>
          </div>
        </div>

        {showManagementControls && (
          <div className={cn(
            "absolute top-1.5 right-1.5 z-20 transition-opacity duration-300",
            isImageLoading && 'opacity-0'
          )}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-full bg-black/40 hover:bg-black/60 text-white hover:text-primary-foreground focus-visible:ring-1 focus-visible:ring-ring backdrop-blur-sm"
                  onClick={handleDropdownInteraction}
                  aria-label={t('characterActions')}
                >
                  <MoreVertical size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                onClick={handleDropdownInteraction}
                className="bg-popover text-popover-foreground border-[hsl(var(--glass-border-soft))] shadow-xl backdrop-blur-md rounded-xl"
              >
                <DropdownMenuItem onSelect={() => onEdit?.(character.id)} className="cursor-pointer hover:!bg-white/10 focus:!bg-white/10">
                  <Edit3 className="mr-2 h-4 w-4" />
                  <span>{t('editCharacter')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onToggleVisibility?.(character.id)} className="cursor-pointer hover:!bg-white/10 focus:!bg-white/10">
                  {character.visibility === 'public' ? (
                    <EyeOff className="mr-2 h-4 w-4" />
                  ) : (
                    <Eye className="mr-2 h-4 w-4" />
                  )}
                  <span>{character.visibility === 'public' ? t('makePrivate') : t('makePublic')}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[hsl(var(--glass-border-soft))]" />
                <DropdownMenuItem onSelect={() => onDelete?.(character)} className="text-destructive focus:text-destructive focus:!bg-destructive/20 cursor-pointer">
                  <Trash2 className="mr-2 h-4 w-4" />
                  <span>{t('deleteCharacter')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {character.visibility === 'private' && !showManagementControls && (
            <div className={cn(
              "absolute top-1.5 left-1.5 bg-black/40 text-white px-1 py-px rounded-full text-[0.6rem] flex items-center gap-0.5 backdrop-blur-sm z-20 shadow transition-opacity duration-300",
              isImageLoading && 'opacity-0'
            )}>
                <EyeOff size={10} />
                <span className="ml-0.5">{t('private')}</span>
            </div>
        )}
      </div>
    </Card>
  );

  return (
    <Link href={`/chat/${character.username || character.id}`} className="block h-full">
      {cardContent}
    </Link>
  );
};

export default memo(CharacterCard);